"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function () {
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: numeralRef,
    isVisible: numeralVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: labelRef,
    isVisible: labelVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: headingRef,
    isVisible: headingVisible
  } = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.2
  });
  const {
    ref: para1Ref,
    isVisible: para1Visible
  } = useRevealOnScroll<HTMLParagraphElement>({
    threshold: 0.2
  });
  const {
    ref: para2Ref,
    isVisible: para2Visible
  } = useRevealOnScroll<HTMLParagraphElement>({
    threshold: 0.2
  });
  const {
    ref: linkRef,
    isVisible: linkVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const headlineWords = [{
    text: "Life",
    italic: false
  }, {
    text: "can",
    italic: false
  }, {
    text: "be",
    italic: false
  }, {
    text: "messy,",
    italic: true
  }, {
    text: "unpredictable,",
    italic: false
  }, {
    text: "and",
    italic: false
  }, {
    text: "overwhelming.",
    italic: false
  }];
  const paragraph1Words = "At TR Mental Health, we provide a compassionate and judgment‑free space where you can show up exactly as you are and begin creating meaningful change.".split(" ");
  const paragraph2Words = "Our approach honors your unique story, providing evidence-based techniques tailored to your specific needs. You are not alone; we are here to help you rebuild and find the balance you deserve.".split(" ");
  return <section data-section-id="4908" id="a-safe-space-for-every-voice" className="bg-background text-foreground overflow-hidden py-32 md:py-40 lg:py-[200px]">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-0">
          {}
          <div className="lg:col-start-2 lg:col-span-4 lg:mt-20 order-1">
            <div ref={imageRef} className={`relative overflow-hidden rounded-lg transition-all duration-[1400ms] ease-out ${imageVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
              <div className="aspect-[3/4] h-[70vh] lg:h-auto w-full">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699238/img/Screenshot 2026-06-08 100941.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699238/img/Screenshot 2026-06-08 100941.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699238/img/Screenshot 2026-06-08 100941.png-wvc-sizes" alt="Compassionate therapeutic environment" loading="lazy" decoding="async" className="h-full object-cover !w-[394.67px]" />
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-start-8 lg:col-span-5 order-2 relative">
            {}
            <div ref={numeralRef} className={`font-serif text-[120px] md:text-[160px] lg:text-[200px] leading-none font-light text-primary -tracking-tighter mb-2 transition-all duration-1000 ease-out ${numeralVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
            lineHeight: "0.85"
          }}><br /></div>

            {}
            <div ref={labelRef} className={`font-mono text-[12px] uppercase tracking-[0.25em] text-muted-foreground mb-8 mt-6 transition-all duration-700 delay-100 ${labelVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              Our Philosophy
            </div>

            {}
            <h2 ref={headingRef} className="font-default font-light text-4xl md:text-5xl lg:text-[64px] leading-[1.05] text-foreground mb-10" style={{
            letterSpacing: "-0.01em"
          }}>
              {headlineWords.map((word, i) => <span key={i} data-index={i} className={`inline-block mr-3 transition-all duration-700 ease-out ${headingVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"} ${word.italic ? "italic text-accent-foreground" : ""}`} style={{
              transitionDelay: `${i * 60}ms`
            }}>
                  {word.text}
                </span>)}
            </h2>

            {}
            

            {}
            <p ref={para1Ref} className="font-default text-lg md:text-xl leading-relaxed text-foreground/80 max-w-[480px] mb-6">
              {paragraph1Words.map((word, i) => <span key={i} data-index={i} className={`inline-block mr-[0.25em] transition-all duration-500 ease-out ${para1Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"}`} style={{
              transitionDelay: `${i * 60}ms`
            }}>
                  {word}
                </span>)}
            </p>

            {}
            <p ref={para2Ref} className="font-default text-lg md:text-xl leading-relaxed text-foreground/80 max-w-[480px] mb-12">
              {paragraph2Words.map((word, i) => <span key={i} data-index={i} className={`inline-block mr-[0.25em] transition-all duration-500 ease-out ${para2Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"}`} style={{
              transitionDelay: `${i * 60}ms`
            }}>
                  {word}
                </span>)}
            </p>

            {}
            <div ref={linkRef} className={`transition-all duration-700 ease-out ${linkVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <Link to="/AboutMePage" className="font-default inline-flex items-center gap-2 text-base md:text-lg text-foreground border-b border-foreground/40 pb-1 hover:border-accent-foreground hover:text-accent-foreground transition-colors duration-300 group">
                <span className="italic">Learn About Our Therapy</span>
                <ArrowRight className="w-4 h-4 text-accent-foreground transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>;
}